import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { EMPTY, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { AutomacaoService } from '@shared/services/apis/compras/automacao.service';
import { CompraAprovacaoService } from '@shared/services/apis/compras/compra-aprovacao.service';
import { CompraEtapaService } from '@shared/services/apis/compras/compra-etapa.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { AprovacaoCompraResponse } from '@shared/services/responses/compras/compra-aprovacao.response';
import { TipoAutomacaoEnum } from '@shared/enums/compras/tipo-automacao.enum';
import { TipoOcorrenciaCompraEnum } from '@shared/enums/compras/tipo-ocorrencia-compra.enum';
import type { KanbanStage, Person } from '../../../processo-compras.models';
import {
  calcularSubtotalItemPedido,
  calcularTotalItensPedido,
  calcularTotalPedido,
  calcularTributosItemPedido,
  numToBR,
  PedidoItemView,
  PedidoParcelaView,
  PedidoState,
} from '../card-edit-dialog.types';
import { CedEnvioPedidoComponent, EnviarPedidoPayload } from './ced-envio-pedido.component';

/**
 * Etapa de Aprovação: status/alçada, resumo somente-leitura do pedido já fechado,
 * decisão do aprovador (aprovar / rejeitar com destino + justificativa), envio ao
 * fornecedor (liberado só depois de aprovado) e histórico.
 *
 * Contrato com o shell:
 * - Carrega a aprovação por conta própria — antes da extração o shell a carregava na abertura.
 * - `pedido` é o PedidoState do shell, exibido aqui apenas para leitura.
 * - `backMoves` já vem derivado da ordem do fluxo pelo shell: destinos de uma rejeição.
 * - `(enviarPedido)` é tratado pelo shell — `pedido.envio` é espelho compartilhado.
 * - `(rejeitado)` é emitido com a chave da etapa de destino após a rejeição + movimentação
 *   concluírem; o shell atualiza o card e fecha o diálogo (o filho não conhece o MatDialogRef).
 */
@Component({
  selector: 'app-ced-stage-aprovacao',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    AlertCardComponent,
    LoadingBoxComponent,
    CedEnvioPedidoComponent,
  ],
  templateUrl: './ced-stage-aprovacao.component.html',
  styleUrl: './ced-stage-aprovacao.component.css',
})
export class CedStageAprovacaoComponent implements OnInit, OnDestroy {
  @Input() idCompra?: number | null;
  @Input() usersById?: Map<number, Person>;
  @Input() allStages: KanbanStage[] = [];
  @Input({ required: true }) pedido!: PedidoState;
  @Input() backMoves: string[] = [];
  @Input() justificativaCotacao = '';

  @Output() rejeitado = new EventEmitter<string>();
  /** Situação de aprovação do pedido depois de registrar um voto (a alçada pode exigir mais de um). */
  @Output() aprovacaoRegistrada = new EventEmitter<boolean>();
  @Output() enviarPedido = new EventEmitter<EnviarPedidoPayload>();

  /** Quem resolve os dados do fornecedor — usado pelo envio automático pós-aprovação. */
  @ViewChild(CedEnvioPedidoComponent) private envioPedido?: CedEnvioPedidoComponent;

  aprovacao: AprovacaoCompraResponse | null = null;
  aprovacaoLoading = false;
  /** Qualquer ação de decisão em andamento: trava os botões. Cada botão com spinner tem a sua flag abaixo. */
  aprovacaoSaving = false;
  aprovando = false;
  rejeitando = false;
  cancelandoAprovacao = false;
  aprovacaoJustificativa = '';
  aprovacaoRejeicaoAberta = false;
  aprovacaoRejeicaoDestino = '';

  private readonly subs = new Subscription();

  constructor(
    private compraAprovacaoService: CompraAprovacaoService,
    private compraEtapaService: CompraEtapaService,
    private automacaoService: AutomacaoService,
    private authService: AuthService,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.carregarAprovacao();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private carregarAprovacao(): void {
    if (!this.idCompra) return;
    this.aprovacaoLoading = true;
    this.subs.add(
      this.compraAprovacaoService.getAprovacao(this.idCompra).subscribe({
        next: (res) => {
          this.aprovacaoLoading = false;
          if (res.isError) {
            this.alertService.show('Erro', res.errorDescription || 'Não foi possível carregar a aprovação.', AlertType.error);
            return;
          }
          this.aprovacao = res;
        },
        error: () => {
          this.aprovacaoLoading = false;
          this.alertService.show('Erro', 'Não foi possível carregar a aprovação.', AlertType.error);
        },
      }),
    );
  }

  cotFmt(n: number): string {
    return numToBR(n);
  }

  // ----- Resumo do pedido (somente leitura) -----
  get pedidoItens(): PedidoItemView[] {
    return this.pedido?.itens ?? [];
  }

  get pedidoParcelas(): PedidoParcelaView[] {
    return this.pedido?.parcelas ?? [];
  }

  get pedidoFrete(): number {
    return this.pedido?.frete ?? 0;
  }

  get pedidoOutrasDespesas(): number {
    return this.pedido?.outrasDespesas ?? 0;
  }

  get pedidoItensTotal(): number {
    return this.pedido ? calcularTotalItensPedido(this.pedido) : 0;
  }

  get pedidoTotal(): number {
    return this.pedido ? calcularTotalPedido(this.pedido) : 0;
  }

  pedidoSubtotal(it: PedidoItemView): number {
    return calcularSubtotalItemPedido(it);
  }

  /** Tributos do item (IPI/ST/IBS/CBS) — informativos, fora do subtotal. */
  pedidoTributos(it: PedidoItemView): number {
    return calcularTributosItemPedido(it);
  }

  pedidoDesconto(it: PedidoItemView): number {
    return it.desconto ?? 0;
  }

  /** Preço unitário formatado para exibição. */
  pedidoPrecoUnitario(it: PedidoItemView): string {
    return numToBR(it.preco);
  }

  // ----- Envio ao fornecedor -----
  /** O pedido só pode ir ao fornecedor com a aprovação concluída (ou dispensada pela alçada). */
  get envioLiberado(): boolean {
    const status = this.aprovacao?.statusGeral;
    return status === 'aprovado' || status === 'nao_requerido';
  }

  /** Pedido já enviado ao fornecedor: o backend recusa revogar a aprovação que autorizou o envio. */
  get pedidoEnviado(): boolean {
    return !!this.pedido?.envio?.datetimeEnvio;
  }

  /** O usuário atual tem perfil para aprovar/reprovar a regra aplicável (master ignora). */
  get podeAprovar(): boolean {
    if (!this.aprovacao?.requerAprovacao || this.aprovacao.statusGeral !== 'pendente') return false;
    const me = this.authService.getUserInfos();
    if (me.master) return true;
    const perfis = me.listaIdPerfil || [];
    const permitidos = this.aprovacao.idsPerfilAprovadores || [];
    return permitidos.length === 0 || perfis.some(p => permitidos.includes(p));
  }

  aprovarPedido(): void {
    if (!this.idCompra || this.aprovacaoSaving) return;
    this.aprovacaoSaving = true;
    this.aprovando = true;
    // Expande os perfis notificados antes da ação: o backend não resolve perfil -> usuários.
    this.subs.add(
      this.automacaoService.idsUsuarioDosPerfis(TipoOcorrenciaCompraEnum.Aprovacao).pipe(
        switchMap(idsUsuarioPerfil =>
          this.compraAprovacaoService.registrarAprovacao(this.idCompra!, true, null, idsUsuarioPerfil)),
      ).subscribe({
        next: (res) => {
          this.aprovacaoSaving = false;
          this.aprovando = false;
          if (res.isError) {
            this.alertService.show('Erro', res.errorDescription || 'Não foi possível registrar a aprovação.', AlertType.error);
            return;
          }
          this.aprovacao = res;
          this.aprovacaoRejeicaoAberta = false;
          this.alertService.show('Sucesso', 'Pedido aprovado.', AlertType.success);
          this.aprovacaoRegistrada.emit(res.isAprovado);
          if (res.isAprovado) {
            this.enviarPedidoPorAutomacao();
          }
        },
        error: () => {
          this.aprovacaoSaving = false;
          this.aprovando = false;
          this.alertService.show('Erro', 'Não foi possível registrar a aprovação.', AlertType.error);
        },
      }),
    );
  }

  /**
   * Automação "Enviar pedido ao fornecedor": dispara ao completar a alçada. O e-mail
   * do fornecedor vem do cadastro de Pessoas (resolvido pelo componente de envio), por
   * isso o disparo mora aqui e não no backend.
   */
  private enviarPedidoPorAutomacao(): void {
    if (this.pedidoEnviado) return;

    this.subs.add(
      this.automacaoService.automacaoAtiva(TipoAutomacaoEnum.EnviarPedidoFornecedor).subscribe(automacao => {
        if (!automacao) return;

        switch (this.envioPedido?.enviarSemConfirmacao() ?? 'fornecedor-indisponivel') {
          case 'sem-email':
            this.alertService.show('Atenção',
              'O envio automático do pedido não aconteceu: o fornecedor não tem e-mail cadastrado. Envie manualmente.',
              AlertType.warning);
            break;
          case 'fornecedor-indisponivel':
            this.alertService.show('Atenção',
              'O envio automático do pedido não aconteceu: não foi possível carregar os dados do fornecedor. Use o botão Enviar pedido para tentar de novo.',
              AlertType.warning);
            break;
        }
      }),
    );
  }

  /** Cancela (reverte) a aprovação do usuário atual. Indisponível depois do envio. */
  cancelarAprovacao(): void {
    if (!this.idCompra || this.aprovacaoSaving || this.pedidoEnviado) return;
    this.aprovacaoSaving = true;
    this.cancelandoAprovacao = true;
    this.subs.add(
      this.compraAprovacaoService.reverterAprovacao(this.idCompra).subscribe({
        next: (res) => {
          this.aprovacaoSaving = false;
          this.cancelandoAprovacao = false;
          if (res.isError) {
            this.alertService.show('Erro', res.errorDescription || 'Não foi possível cancelar a aprovação.', AlertType.error);
            return;
          }
          this.aprovacao = res;
          this.alertService.show('Sucesso', 'Aprovação cancelada.', AlertType.success);
        },
        error: () => {
          this.aprovacaoSaving = false;
          this.cancelandoAprovacao = false;
          this.alertService.show('Erro', 'Não foi possível cancelar a aprovação.', AlertType.error);
        },
      }),
    );
  }

  abrirRejeicao(): void {
    this.aprovacaoRejeicaoAberta = true;
    if (!this.aprovacaoRejeicaoDestino) {
      this.aprovacaoRejeicaoDestino = this.rejeicaoDestinos[0]?.key ?? '';
    }
  }

  /** Destinos ao rejeitar: as etapas anteriores no fluxo principal (já derivadas em backMoves). */
  get rejeicaoDestinos(): { key: string; nome: string }[] {
    return this.backMoves.map(k => ({ key: k, nome: this.allStages.find(s => s.key === k)?.name || k }));
  }

  confirmarRejeicao(): void {
    if (!this.idCompra || this.aprovacaoSaving) return;
    const justificativa = this.aprovacaoJustificativa.trim();
    const destino = this.aprovacaoRejeicaoDestino;
    if (!destino) {
      this.alertService.show('Atenção', 'Selecione o destino após a rejeição.', AlertType.warning);
      return;
    }
    if (!justificativa) {
      this.alertService.show('Atenção', 'Informe a justificativa da rejeição.', AlertType.warning);
      return;
    }

    const idCompra = this.idCompra;
    this.aprovacaoSaving = true;
    this.rejeitando = true;
    // 1) registra a reprovação; 2) move o card para o destino escolhido.
    // Encadeado com switchMap (não subscribe aninhado) para que a chamada fique
    // rastreada em this.subs e seja cancelada se o diálogo fechar no meio do fluxo.
    this.subs.add(
      this.automacaoService.idsUsuarioDosPerfis(TipoOcorrenciaCompraEnum.Reprovacao).pipe(
        switchMap(idsUsuarioPerfil =>
          this.compraAprovacaoService.registrarAprovacao(idCompra, false, justificativa, idsUsuarioPerfil)),
        switchMap((res) => {
          if (res.isError) {
            this.aprovacaoSaving = false;
            this.rejeitando = false;
            this.alertService.show('Erro', res.errorDescription || 'Não foi possível registrar a rejeição.', AlertType.error);
            return EMPTY;
          }
          return this.compraEtapaService.movimentarEtapa(idCompra, {
            chaveEtapaDestino: destino,
            justificativa,
          });
        }),
      ).subscribe({
        next: (mov) => {
          this.aprovacaoSaving = false;
          this.rejeitando = false;
          if (mov.isError) {
            this.alertService.show('Atenção', mov.errorDescription || 'Rejeição registrada, mas não foi possível mover o card.', AlertType.warning);
            return;
          }
          // O shell atualiza o card e fecha o diálogo.
          this.rejeitado.emit(mov.chaveEtapa || destino);
        },
        error: () => {
          this.aprovacaoSaving = false;
          this.rejeitando = false;
          this.alertService.show('Atenção', 'Rejeição registrada, mas não foi possível mover o card.', AlertType.warning);
        },
      }),
    );
  }

  aprovacaoNomeAprovador(idUsuario: number): string {
    return this.usersById?.get(idUsuario)?.name || `Usuário ${idUsuario}`;
  }

  get aprovacaoStatusLabel(): string {
    switch (this.aprovacao?.statusGeral) {
      case 'aprovado': return 'Aprovado';
      case 'reprovado': return 'Reprovado';
      case 'nao_requerido': return 'Não requer aprovação';
      default: return 'Aguardando aprovação';
    }
  }
}
